import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import { toast } from 'react-toastify';
import { openFilePreview } from '../common/FilePreviewModal';
import { getAuthHeaders } from '../../auth';
import useSortableTable from '../common/useSortableTable';
import TableScrollContainer from '../common/TableScrollContainer';

const API_BASE = '/api/documentation/jobs';

const toUpperDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value.toString().toUpperCase();
};

const CopyCell = ({ text, children }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = (e) => {
        e.stopPropagation();
        const val = text != null ? String(text) : (typeof children === 'string' ? children : '');
        if (!val || val === '-') return;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(val).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            });
        } else {
            const ta = document.createElement('textarea');
            ta.value = val;
            ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
            document.body.removeChild(ta);
        }
    };
    return (
        <span className="copy-cell">
            {children}
            <button
                type="button"
                className={`copy-icon-btn${copied ? ' copied' : ''}`}
                title={copied ? 'Copied!' : 'Copy'}
                onClick={handleCopy}
                onMouseDown={e => e.stopPropagation()}
            >
                {copied
                    ? <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    : <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                }
            </button>
        </span>
    );
};

const formatDoStatus = (doStatuses) => {
  if (!doStatuses || !Array.isArray(doStatuses) || doStatuses.length === 0) return '-';
  const status = doStatuses[0];
  if (!status) return '-';
  return status.toString().toUpperCase();
};

const getTextColorForBackground = (hexColor) => {
    if (!hexColor || hexColor === '#FFFFFF') return '#000000';
    const rgb = parseInt(hexColor.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma < 128 ? '#FFFFFF' : '#000000';
};

const DnRoReportTab = ({ hasPermission, onCountChange, reportType = 'port' }) => {
    const isAirport = reportType === 'airport';
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [appliedFromDate, setAppliedFromDate] = useState('');
    const [appliedToDate, setAppliedToDate] = useState('');
    const [idfFilter, setIdfFilter] = useState('');
    const [search, setSearch] = useState('');
    const [icdFilter, setIcdFilter] = useState('');
    const [terminalFilter, setTerminalFilter] = useState('');
    const [dropdowns, setDropdowns] = useState({ icds: [], terminals: [] });

    const [uploadModal, setUploadModal] = useState({ open: false, row: null });
    const [releaseOrderFile, setReleaseOrderFile] = useState(null);
    const [releaseOrderPreview, setReleaseOrderPreview] = useState(null);
    const [deliveryNoteFile, setDeliveryNoteFile] = useState(null);
    const [deliveryNotePreview, setDeliveryNotePreview] = useState(null);
    const [saving, setSaving] = useState(false);

    const canEdit = hasPermission(isAirport ? 'AIRPORT REPORTS' : 'PORT REPORTS', 'can_edit');

    const fetchDropdowns = useCallback(async () => {
        try {
            const endpoint = isAirport ? `${API_BASE}/airport-dropdowns/` : `${API_BASE}/port-dropdowns/`;
            const res = await fetch(endpoint, { headers: getAuthHeaders(false) });
            if (res.ok) {
                const data = await res.json();
                if (isAirport) {
                    setDropdowns({ terminals: data.terminals || [] });
                } else {
                    setDropdowns({ icds: data.icds || [] });
                }
            }
        } catch (e) {
            console.error(e);
        }
    }, [isAirport]);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const endpoint = isAirport ? `${API_BASE}/airport-report/` : `${API_BASE}/port-report/`;
            const res = await fetch(`${endpoint}?dn_ro_only=true`, { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error('Failed to fetch DN/RO report');
            const data = await res.json();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            toast.error(e.message || 'Failed to fetch DN/RO report');
        } finally {
            setLoading(false);
        }
    }, [isAirport]);

    useEffect(() => {
        fetchDropdowns();
        fetchRows();
    }, [fetchDropdowns, fetchRows]);

    const filteredRows = useMemo(() => {
        let data = [...rows];
        if (appliedFromDate && appliedToDate) {
            const from = new Date(appliedFromDate);
            const to = new Date(appliedToDate);
            data = data.filter(r => {
                if (!r.release_date) return false;
                const d = new Date(r.release_date);
                return d >= from && d <= to;
            });
        }
        if (idfFilter.trim()) {
            const n = idfFilter.trim().toUpperCase();
            data = data.filter(r => (r.idf_no || '').toUpperCase().includes(n));
        }
        if (isAirport) {
            if (terminalFilter) {
                data = data.filter(r => (r.terminal || '').toUpperCase() === terminalFilter.toUpperCase());
            }
        } else if (icdFilter) {
            data = data.filter(r => (r.icd || '').toUpperCase() === icdFilter.toUpperCase());
        }
        if (search.trim()) {
            const needle = search.toUpperCase();
            data = data.filter(r => {
                const searchFields = isAirport
                    ? [r.id, r.idf_no, r.job_no, r.awb, r.airline, r.flight_number, r.importer, r.terminal, r.transport, r.remarks]
                    : [r.id, r.idf_no, r.kk_file_no, r.customer_name, r.vessel, r.client_ref, r.material, r.icd];
                return searchFields.some(field => field && field.toString().toUpperCase().includes(needle));
            });
        }
        return data;
    }, [rows, appliedFromDate, appliedToDate, idfFilter, icdFilter, terminalFilter, search, isAirport]);

    useEffect(() => {
        if (onCountChange) onCountChange(filteredRows.length);
    }, [filteredRows, onCountChange]);

    const { items: sortedRows, requestSort, getSortIndicator } = useSortableTable(filteredRows);

    const openUploadModal = (row) => {
        setUploadModal({ open: true, row });
        setReleaseOrderFile(null);
        setReleaseOrderPreview(null);
        setDeliveryNoteFile(null);
        setDeliveryNotePreview(null);
    };

    const closeUploadModal = () => {
        if (releaseOrderPreview) URL.revokeObjectURL(releaseOrderPreview);
        if (deliveryNotePreview) URL.revokeObjectURL(deliveryNotePreview);
        setUploadModal({ open: false, row: null });
        setReleaseOrderFile(null);
        setReleaseOrderPreview(null);
        setDeliveryNoteFile(null);
        setDeliveryNotePreview(null);
    };

    const handleFileChange = (type, file) => {
        if (type === 'releaseOrder') {
            if (releaseOrderPreview) URL.revokeObjectURL(releaseOrderPreview);
            setReleaseOrderFile(file);
            setReleaseOrderPreview(file ? URL.createObjectURL(file) : null);
        } else {
            if (deliveryNotePreview) URL.revokeObjectURL(deliveryNotePreview);
            setDeliveryNoteFile(file);
            setDeliveryNotePreview(file ? URL.createObjectURL(file) : null);
        }
    };

    const handleSaveDocument = async () => {
        if (!uploadModal.row) {
            toast.error('No job selected.');
            return;
        }
        if (!canEdit) {
            toast.error('You do not have permission to upload documents.');
            return;
        }
        if (!releaseOrderFile && !deliveryNoteFile) {
            toast.error('Please select at least one file to upload.');
            return;
        }
        setSaving(true);
        try {
            const formData = new FormData();
            let index = 0;
            if (releaseOrderFile) {
                formData.append(`file_${index}`, releaseOrderFile);
                formData.append(`doc_${index}`, JSON.stringify({
                    document_type: 'Release Order',
                    remarks: `Uploaded from DN/RO report`
                }));
                index += 1;
            }
            if (deliveryNoteFile) {
                formData.append(`file_${index}`, deliveryNoteFile);
                formData.append(`doc_${index}`, JSON.stringify({
                    document_type: 'Delivery Note',
                    remarks: `Uploaded from DN/RO report`
                }));
                index += 1;
            }
            const res = await fetch(`${API_BASE}/${uploadModal.row.id}/save-documents/`, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: formData,
            });
            if (!res.ok) throw new Error('Failed to save documents');
            toast.success('Documents uploaded successfully');
            closeUploadModal();
            fetchRows();
        } catch (e) {
            console.error(e);
            toast.error(e.message || 'Failed to save documents');
        } finally {
            setSaving(false);
        }
    };

    const renderDocViewCell = (docField) => {
        return (row) => {
            const doc = row[docField];
            if (doc && doc.document_file_url) {
                return (
                    <button
                        type="button"
                        className="btn-small btn-info-small"
                        onClick={() => openFilePreview(doc.document_file_url, doc.document_file_url.split('/').pop())}
                    >
                        View
                    </button>
                );
            }
            return '-';
        };
    };

    const renderUploadCell = (row) => {
        if (!canEdit) return '-';
        return (
            <button
                type="button"
                onClick={() => openUploadModal(row)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                title="Upload DN/RO"
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
            </button>
        );
    };

    const renderPortRow = (r) => {
        const icdColor = r.icd_color || '#FFFFFF';
        const icdTextColor = getTextColorForBackground(icdColor);
        return (
            <tr key={r.id}>
                <td title={`Job ID: ${r.id}, KK File: ${r.kk_file_no || 'N/A'}`} style={{ position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#fff', userSelect: 'text' }}>
                    <CopyCell text={r.idf_no}>{toUpperDisplay(r.idf_no)}</CopyCell>
                </td>
                <td style={{ whiteSpace: 'pre-line', userSelect: 'text' }}>
                    <CopyCell text={`${r.vessel || ''} ${r.voyage_number || ''}`.trim()}>
                        <span><span style={{ fontWeight: 'bold' }}>{toUpperDisplay(r.vessel)}</span><br />
                        <span style={{ fontWeight: 'normal' }}>{toUpperDisplay(r.voyage_number)}</span></span>
                    </CopyCell>
                </td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.client_ref}>{toUpperDisplay(r.client_ref)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.lcl_gw_dg}>{toUpperDisplay(r.lcl_gw_dg)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.container_size}>{toUpperDisplay(r.container_size)}</CopyCell></td>
                <td style={{ backgroundColor: icdColor, color: icdTextColor, userSelect: 'text' }}>
                    <CopyCell text={r.icd}><span style={{ fontWeight: 'bold' }}>{toUpperDisplay(r.icd)}</span></CopyCell>
                </td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.customer_name}>{toUpperDisplay(r.customer_name)}</CopyCell></td>
                <td><CopyCell text={r.wharfage_status}>{toUpperDisplay(r.wharfage_status)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.bl_type}>{toUpperDisplay(r.bl_type)}</CopyCell></td>
                <td style={{ fontSize: 11, userSelect: 'text' }}><CopyCell text={formatDoStatus(r.do_statuses)}>{formatDoStatus(r.do_statuses)}</CopyCell></td>
                <td style={{ minWidth: 90, userSelect: 'text' }} title={r.material || ''}>
                    <CopyCell text={r.material}>
                        <span style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {r.material && r.material.length > 20
                                ? toUpperDisplay(r.material.slice(0, 20)) + '...'
                                : toUpperDisplay(r.material)}
                        </span>
                    </CopyCell>
                </td>
                <td style={{ userSelect: 'text' }}>
                    {r.port_staff_names && r.port_staff_names.length > 0 ? r.port_staff_names.join(', ') : '-'}
                </td>
                <td style={{ userSelect: 'text' }}>
                    {r.port_transporters && r.port_transporters.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: '1.2em', fontSize: '12px' }}>
                            {r.port_transporters.map((t, i) => (
                                <li key={i} title={t.tooltip || ''}>
                                    <CopyCell text={t.display_text}>{toUpperDisplay(t.display_text)}</CopyCell>
                                </li>
                            ))}
                        </ul>
                    ) : '-'}
                </td>
                <td style={{ whiteSpace: 'nowrap', fontWeight: 600, userSelect: 'text' }}>
                    <CopyCell text={r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : ''}>
                        {r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : '—'}
                    </CopyCell>
                </td>
                <td>{renderDocViewCell('release_order_doc')(r)}</td>
                <td>{renderDocViewCell('delivery_note_doc')(r)}</td>
                <td>{renderUploadCell(r)}</td>
            </tr>
        );
    };

    const renderAirportRow = (r) => {
        const releaseDate = r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : '—';
        return (
            <tr key={r.id}>
                <td style={{ position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#fff', userSelect: 'text' }} title={`Job ID: ${r.id}`}>
                    <CopyCell text={r.idf_no}>{toUpperDisplay(r.idf_no)}</CopyCell>
                </td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.job_no}>{toUpperDisplay(r.job_no)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.awb}>{toUpperDisplay(r.awb)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={String(r.packages || '')}>{r.packages ?? '-'}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={String(r.weight || '')}>{r.weight ?? '-'}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.airline}>{toUpperDisplay(r.airline)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.flight_number}>{toUpperDisplay(r.flight_number)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}>
                    <CopyCell text={r.eta}>{toUpperDisplay(r.eta)}</CopyCell>
                </td>
                <td style={{ userSelect: 'text' }}>
                    <CopyCell text={r.importer}>
                        <span>{toUpperDisplay(r.importer)}{r.is_fund_request && !r.fund_request_released && <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginLeft: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />}</span>
                    </CopyCell>
                </td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.duty_status}>{toUpperDisplay(r.duty_status)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.terminal}>{toUpperDisplay(r.terminal)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.transport}>{toUpperDisplay(r.transport)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.remarks}>{toUpperDisplay(r.remarks)}</CopyCell></td>
                <td style={{ whiteSpace: 'nowrap', fontWeight: 600, userSelect: 'text' }}>
                    <CopyCell text={releaseDate}>{releaseDate}</CopyCell>
                </td>
                <td>{renderDocViewCell('release_order_doc')(r)}</td>
                <td>{renderDocViewCell('delivery_note_doc')(r)}</td>
                <td>{renderUploadCell(r)}</td>
            </tr>
        );
    };

    return (
        <div style={{ overflowX: 'auto' }}>
            <div className="toolbar" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Released Date</label>
                <input
                    type="date"
                    className="search-input"
                    value={fromDate}
                    onChange={e => setFromDate(e.target.value)}
                    style={{ minWidth: 130 }}
                    title="From Released Date"
                />
                <span style={{ color: '#666', fontWeight: 700 }}>To</span>
                <input
                    type="date"
                    className="search-input"
                    value={toDate}
                    onChange={e => setToDate(e.target.value)}
                    style={{ minWidth: 130 }}
                    title="To Released Date"
                />
                <button
                    className="add-btn"
                    style={{ background: '#007bff', padding: '4px 14px' }}
                    onClick={() => { setAppliedFromDate(fromDate); setAppliedToDate(toDate); }}
                >Apply</button>
                <input
                    className="search-input"
                    placeholder="IDF Search"
                    value={idfFilter}
                    onChange={e => setIdfFilter(e.target.value)}
                    style={{ minWidth: 130 }}
                />
                {isAirport ? (
                    <Select2Dropdown
                        options={[{ id: '', text: 'All Terminals' }, ...dropdowns.terminals.map(t => ({ id: t.id, text: t.text }))]}
                        value={terminalFilter}
                        onChange={val => setTerminalFilter(val)}
                        valueKey="id"
                        displayKey="text"
                        style={{ minWidth: 150 }}
                        placeholder="Terminal Filter"
                    />
                ) : (
                    <Select2Dropdown
                        options={[{ id: '', text: 'All ICDs' }, ...dropdowns.icds.map(i => ({ id: i.id, text: i.name }))]}
                        value={icdFilter}
                        onChange={val => setIcdFilter(val)}
                        valueKey="id"
                        displayKey="text"
                        style={{ minWidth: 150 }}
                        placeholder="ICD Filter"
                    />
                )}
                <input
                    className="search-input"
                    placeholder="General Search"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ minWidth: 200 }}
                />
                <button
                    className="add-btn"
                    style={{ background: '#6c757d', whiteSpace: 'nowrap' }}
                    onClick={() => {
                        setFromDate(''); setToDate(''); setAppliedFromDate(''); setAppliedToDate('');
                        setIdfFilter(''); setSearch(''); setIcdFilter(''); setTerminalFilter('');
                    }}
                >Clear All Filters</button>
                <button
                    className="add-btn"
                    style={{ background: '#28a745', whiteSpace: 'nowrap' }}
                    onClick={fetchRows}
                    title="Refresh"
                >Refresh</button>
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                Showing {sortedRows.length} of {rows.length} records
            </div>

            <TableScrollContainer>
                <table className="jobs-table" style={{ overflow: 'visible' }}>
                    <thead>
                        {isAirport ? (
                            <tr>
                                <th onClick={() => requestSort('idf_no')} style={{ position: 'sticky', left: 0, zIndex: 20, backgroundColor: '#f8f9fa' }}>IDF NO {getSortIndicator('idf_no')}</th>
                                <th onClick={() => requestSort('job_no')}>JOB NO {getSortIndicator('job_no')}</th>
                                <th onClick={() => requestSort('awb')}>AWB {getSortIndicator('awb')}</th>
                                <th onClick={() => requestSort('packages')}>PACKAGES {getSortIndicator('packages')}</th>
                                <th onClick={() => requestSort('weight')}>WEIGHT {getSortIndicator('weight')}</th>
                                <th onClick={() => requestSort('airline')}>AIRLINE {getSortIndicator('airline')}</th>
                                <th onClick={() => requestSort('flight_number')}>FLIGHT NUMBER {getSortIndicator('flight_number')}</th>
                                <th onClick={() => requestSort('eta')}>ETA {getSortIndicator('eta')}</th>
                                <th onClick={() => requestSort('importer')}>IMPORTER {getSortIndicator('importer')}</th>
                                <th onClick={() => requestSort('duty_status')}>DUTY PAID {getSortIndicator('duty_status')}</th>
                                <th onClick={() => requestSort('terminal')}>TERMINAL {getSortIndicator('terminal')}</th>
                                <th onClick={() => requestSort('transport')}>TRANSPORT {getSortIndicator('transport')}</th>
                                <th onClick={() => requestSort('remarks')}>REMARKS {getSortIndicator('remarks')}</th>
                                <th onClick={() => requestSort('release_date')} style={{ whiteSpace: 'nowrap' }}>RELEASED DATE {getSortIndicator('release_date')}</th>
                                <th>RELEASE ORDER</th>
                                <th>DELIVERY NOTE</th>
                                <th>UPLOAD</th>
                            </tr>
                        ) : (
                            <tr>
                                <th onClick={() => requestSort('idf_no')} style={{ position: 'sticky', left: 0, zIndex: 20, backgroundColor: '#f8f9fa' }}>IDF NO {getSortIndicator('idf_no')}</th>
                                <th style={{ whiteSpace: 'pre-line' }} onClick={() => requestSort('vessel')}>
                                    <span style={{ fontWeight: 'bold' }}>VESSEL</span> {getSortIndicator('vessel')}<br />
                                    <span style={{ fontWeight: 'normal' }}>VOYAGE</span>
                                </th>
                                <th onClick={() => requestSort('client_ref')}>CLIENT REF {getSortIndicator('client_ref')}</th>
                                <th onClick={() => requestSort('lcl_gw_dg')}>LCL GW / DG {getSortIndicator('lcl_gw_dg')}</th>
                                <th onClick={() => requestSort('container_size')}>CONTAINER SIZE {getSortIndicator('container_size')}</th>
                                <th onClick={() => requestSort('icd')}>ICD {getSortIndicator('icd')}</th>
                                <th onClick={() => requestSort('customer_name')}>CUSTOMER NAME {getSortIndicator('customer_name')}</th>
                                <th onClick={() => requestSort('wharfage_status')}>WHARFAGE {getSortIndicator('wharfage_status')}</th>
                                <th onClick={() => requestSort('bl_type')}>BL TYPE {getSortIndicator('bl_type')}</th>
                                <th onClick={() => requestSort('do_statuses')}>DO STATUS {getSortIndicator('do_statuses')}</th>
                                <th onClick={() => requestSort('material')} style={{ minWidth: 90, whiteSpace: 'normal', wordBreak: 'break-word' }}>MATERIAL {getSortIndicator('material')}</th>
                                <th onClick={() => requestSort('port_staff_names')}>STAFF {getSortIndicator('port_staff_names')}</th>
                                <th onClick={() => requestSort('port_transporters')}>TRANSPORTER {getSortIndicator('port_transporters')}</th>
                                <th onClick={() => requestSort('release_date')} style={{ whiteSpace: 'nowrap' }}>RELEASED DATE {getSortIndicator('release_date')}</th>
                                <th>RELEASE ORDER</th>
                                <th>DELIVERY NOTE</th>
                                <th>UPLOAD</th>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={17} style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                                        <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                        <div style={{ color: '#666', fontSize: '14px' }}>Loading DN/RO report data...</div>
                                    </div>
                                    <style>{`
                                        @keyframes spin {
                                            0% { transform: rotate(0deg); }
                                            100% { transform: rotate(360deg); }
                                        }
                                    `}</style>
                                </td>
                            </tr>
                        )}
                        {!loading && sortedRows.length === 0 && (
                            <tr><td colSpan={17} className="no-data">No records found.</td></tr>
                        )}
                        {!loading && sortedRows.map(r => (isAirport ? renderAirportRow(r) : renderPortRow(r)))}
                    </tbody>
                </table>
            </TableScrollContainer>

            {uploadModal.open && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: 500 }}>
                        <div className="dialog-header">
                            <h3>Upload DN/RO Documents</h3>
                        </div>
                        <div className="dialog-body">
                            <p style={{ marginBottom: 16 }}><strong>Job IDF:</strong> {uploadModal.row?.idf_no}</p>
                            <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12, marginBottom: 12 }}>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>Release Order</label>
                                {releaseOrderFile ? (
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <span style={{ flex: 1, fontSize: '13px' }}>{releaseOrderFile.name}</span>
                                        <button
                                            type="button"
                                            className="btn-small btn-info-small"
                                            onClick={() => openFilePreview(releaseOrderPreview, releaseOrderFile.name)}
                                        >
                                            View
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-small btn-danger-small"
                                            onClick={() => handleFileChange('releaseOrder', null)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <input
                                        type="file"
                                        className="form-control"
                                        onChange={(e) => handleFileChange('releaseOrder', e.target.files[0])}
                                        disabled={saving}
                                    />
                                )}
                            </div>
                            <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12 }}>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>Delivery Note</label>
                                {deliveryNoteFile ? (
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <span style={{ flex: 1, fontSize: '13px' }}>{deliveryNoteFile.name}</span>
                                        <button
                                            type="button"
                                            className="btn-small btn-info-small"
                                            onClick={() => openFilePreview(deliveryNotePreview, deliveryNoteFile.name)}
                                        >
                                            View
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-small btn-danger-small"
                                            onClick={() => handleFileChange('deliveryNote', null)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <input
                                        type="file"
                                        className="form-control"
                                        onChange={(e) => handleFileChange('deliveryNote', e.target.files[0])}
                                        disabled={saving}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="dialog-footer">
                            <button type="button" className="dialog-btn dialog-btn-secondary" onClick={closeUploadModal} disabled={saving}>Cancel</button>
                            <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleSaveDocument} disabled={saving || (!releaseOrderFile && !deliveryNoteFile)}>
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DnRoReportTab;
